import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, Save, Eye, Smartphone, Monitor, Plus, Trash2, 
  GripVertical, Image, Type, MapPin, Clock, Calendar, 
  FileText, HelpCircle, CheckCircle, AlertCircle, ChevronDown,
  ChevronUp, Settings, Gift, DollarSign, Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// Section Templates for Experiences
const SECTION_TEMPLATES = [
  { id: 'hero', name: 'Hero Banner', icon: Image, description: 'Eye-catching header with image' },
  { id: 'about', name: 'The Experience', icon: FileText, description: 'Hook, description, and vibe tags' },
  { id: 'highlights', name: 'Highlights', icon: Star, description: 'Key features and benefits' },
  { id: 'includes', name: 'What\'s Included', icon: CheckCircle, description: 'List of inclusions' },
  { id: 'venue', name: 'Venue Info', icon: MapPin, description: 'Location and venue details' },
  { id: 'validity', name: 'Validity & Terms', icon: Calendar, description: 'Expiration and terms' },
  { id: 'howto', name: 'How to Redeem', icon: Gift, description: 'Redemption instructions' },
  { id: 'faq', name: 'FAQ', icon: HelpCircle, description: 'Frequently asked questions' },
  { id: 'gallery', name: 'Photo Gallery', icon: Image, description: 'Additional images' },
];

// Default sections for a new experience
const DEFAULT_SECTIONS = [
  { 
    id: 'hero-1', 
    type: 'hero', 
    order: 0,
    data: { 
      title: '', 
      subtitle: '', 
      image_url: '', 
      overlay_opacity: 40 
    } 
  },
  { 
    id: 'about-1', 
    type: 'about', 
    order: 1,
    data: { 
      hook: '',
      content: '',
      vibe_tags: []
    } 
  },
  { 
    id: 'includes-1', 
    type: 'includes', 
    order: 2,
    data: { 
      title: "What's Included",
      items: []
    } 
  },
  { 
    id: 'venue-1', 
    type: 'venue', 
    order: 3,
    data: { 
      show_map: true 
    } 
  },
];

// Section Editor Components
const HeroEditor = ({ data, onChange }) => (
  <div className="space-y-4">
    <div>
      <Label>Hero Image URL</Label>
      <Input 
        placeholder="https://example.com/image.jpg"
        value={data.image_url || ''}
        onChange={(e) => onChange({ ...data, image_url: e.target.value })}
      />
      <p className="text-xs text-muted-foreground mt-1">Recommended: 1920x1080px</p>
    </div>
    <div>
      <Label>Subtitle (optional)</Label>
      <Input 
        placeholder="A brief tagline for your experience"
        value={data.subtitle || ''}
        onChange={(e) => onChange({ ...data, subtitle: e.target.value })}
      />
    </div>
    <div>
      <Label>Overlay Opacity: {data.overlay_opacity || 40}%</Label>
      <input 
        type="range" 
        min="0" 
        max="80" 
        value={data.overlay_opacity || 40}
        onChange={(e) => onChange({ ...data, overlay_opacity: parseInt(e.target.value) })}
        className="w-full"
      />
    </div>
  </div>
);

const VIBE_OPTIONS = ['Educational','Exclusive','Behind-the-scenes','Romantic','Social','Adventurous','Relaxing','Creative','Hands-on','VIP','Foodie','Nightlife','Cultural','Outdoors'];

const AboutEditor = ({ data, onChange }) => {
  const vibeTags = data.vibe_tags || [];
  const toggleVibe = (tag) => {
    const updated = vibeTags.includes(tag) ? vibeTags.filter(t => t !== tag) : [...vibeTags, tag];
    onChange({ ...data, vibe_tags: updated });
  };
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-indigo-600 font-semibold">The Hook</Label>
        <Input placeholder="One compelling sentence — the elevator pitch" value={data.hook || ''} onChange={(e) => onChange({ ...data, hook: e.target.value })} />
        <p className="text-xs text-muted-foreground mt-1">Shows under the experience title</p>
      </div>
      <div>
        <Label className="text-indigo-600 font-semibold">The Experience</Label>
        <Textarea rows={5} placeholder="Full description of what happens during the experience..." value={data.content || ''} onChange={(e) => onChange({ ...data, content: e.target.value })} />
      </div>
      <div>
        <Label className="text-indigo-600 font-semibold">The Vibe</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {[...new Set([...VIBE_OPTIONS, ...vibeTags])].map(tag => (
            <span key={tag} onClick={() => toggleVibe(tag)} className={`px-3 py-1 rounded-full text-xs cursor-pointer transition-colors ${vibeTags.includes(tag) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-indigo-100'}`}>{tag}</span>
          ))}
        </div>
        <Input placeholder="Add custom vibe (Enter)" className="mt-2 h-8 text-xs" onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) { onChange({ ...data, vibe_tags: [...vibeTags, e.target.value.trim()] }); e.target.value = ''; }}} />
      </div>
    </div>
  );
};

const IncludesEditor = ({ data, onChange }) => {
  const items = data.items || [];

  const addItem = () => {
    onChange({ ...data, items: [...items, ''] });
  };

  const updateItem = (index, value) => {
    const newItems = [...items];
    newItems[index] = value;
    onChange({ ...data, items: newItems });
  };

  const removeItem = (index) => {
    onChange({ ...data, items: items.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || "What's Included"}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label>Included Items</Label>
        {items.map((item, index) => (
          <div key={index} className="flex gap-2">
            <Input 
              placeholder="e.g., 3-course dinner for two"
              value={item}
              onChange={(e) => updateItem(index, e.target.value)}
            />
            <Button variant="ghost" size="icon" onClick={() => removeItem(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addItem}>
          <Plus className="w-4 h-4 mr-2" />
          Add Item
        </Button>
      </div>
    </div>
  );
};

const HighlightsEditor = ({ data, onChange }) => {
  const highlights = data.highlights || [];

  const addHighlight = () => {
    onChange({ 
      ...data, 
      highlights: [...highlights, { icon: 'star', title: '', description: '' }] 
    });
  };

  const updateHighlight = (index, field, value) => {
    const newHighlights = [...highlights];
    newHighlights[index] = { ...newHighlights[index], [field]: value };
    onChange({ ...data, highlights: newHighlights });
  };

  const removeHighlight = (index) => {
    onChange({ ...data, highlights: highlights.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Highlights'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-3">
        {highlights.map((highlight, index) => (
          <Card key={index} className="p-3">
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium">Highlight {index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeHighlight(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Input 
                placeholder="Title"
                value={highlight.title}
                onChange={(e) => updateHighlight(index, 'title', e.target.value)}
              />
              <Input 
                placeholder="Description"
                value={highlight.description}
                onChange={(e) => updateHighlight(index, 'description', e.target.value)}
              />
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addHighlight}>
          <Plus className="w-4 h-4 mr-2" />
          Add Highlight
        </Button>
      </div>
    </div>
  );
};

const HowToEditor = ({ data, onChange }) => {
  const steps = data.steps || [];

  const addStep = () => {
    onChange({ ...data, steps: [...steps, { number: steps.length + 1, text: '' }] });
  };

  const updateStep = (index, text) => {
    const newSteps = [...steps];
    newSteps[index] = { number: index + 1, text };
    onChange({ ...data, steps: newSteps });
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, number: i + 1 }));
    onChange({ ...data, steps: newSteps });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'How to Redeem'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        {steps.map((step, index) => (
          <div key={index} className="flex gap-2 items-center">
            <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-sm">
              {index + 1}
            </span>
            <Input 
              placeholder="Describe this step..."
              value={step.text}
              onChange={(e) => updateStep(index, e.target.value)}
            />
            <Button variant="ghost" size="icon" onClick={() => removeStep(index)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" onClick={addStep}>
          <Plus className="w-4 h-4 mr-2" />
          Add Step
        </Button>
      </div>
    </div>
  );
};

const FAQEditor = ({ data, onChange }) => {
  const faqs = data.faqs || [];

  const addFaq = () => {
    onChange({ ...data, faqs: [...faqs, { question: '', answer: '' }] });
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index] = { ...newFaqs[index], [field]: value };
    onChange({ ...data, faqs: newFaqs });
  };

  const removeFaq = (index) => {
    onChange({ ...data, faqs: faqs.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Section Title</Label>
        <Input 
          value={data.title || 'Frequently Asked Questions'}
          onChange={(e) => onChange({ ...data, title: e.target.value })}
        />
      </div>
      <div className="space-y-3">
        {faqs.map((faq, index) => (
          <Card key={index} className="p-3">
            <div className="flex justify-between items-start mb-2">
              <span className="text-sm font-medium">Question {index + 1}</span>
              <Button variant="ghost" size="sm" onClick={() => removeFaq(index)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2">
              <Input 
                placeholder="Question"
                value={faq.question}
                onChange={(e) => updateFaq(index, 'question', e.target.value)}
              />
              <Textarea 
                rows={2}
                placeholder="Answer"
                value={faq.answer}
                onChange={(e) => updateFaq(index, 'answer', e.target.value)}
              />
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addFaq}>
          <Plus className="w-4 h-4 mr-2" />
          Add FAQ
        </Button>
      </div>
    </div>
  );
};

const GalleryEditor = ({ data, onChange }) => {
  const images = data.images || [];

  const addImage = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      onChange({ ...data, images: [...images, url] });
    }
  };

  const removeImage = (index) => {
    onChange({ ...data, images: images.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <Label>Gallery Images</Label>
      <div className="grid grid-cols-3 gap-2">
        {images.map((url, index) => (
          <div key={index} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button 
              onClick={() => removeImage(index)}
              className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ))}
        <button 
          onClick={addImage}
          className="aspect-square border-2 border-dashed rounded-lg flex items-center justify-center text-muted-foreground hover:border-purple-500 hover:text-purple-500 transition-colors"
        >
          <Plus className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
};

// Live Preview Component
const LivePreview = ({ experience, sections, viewMode }) => {
  const getSectionContent = (section) => {
    switch (section.type) {
      case 'hero':
        return (
          <div 
            className="relative h-72 bg-cover bg-center flex items-end"
            style={{ backgroundImage: `url(${section.data.image_url || 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1200'})` }}
          >
            <div 
              className="absolute inset-0 bg-black"
              style={{ opacity: (section.data.overlay_opacity || 40) / 100 }}
            />
            <div className="relative z-10 p-6 w-full">
              <h1 className="text-2xl font-bold text-white mb-1">{experience?.title || 'Experience Title'}</h1>
              {section.data.subtitle && (
                <p className="text-white/80">{section.data.subtitle}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-white/80 text-sm">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {experience?.venue_name}
                </span>
                {experience?.valid_until && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Valid until {format(new Date(experience.valid_until), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      
      case 'about':
        return (
          <div className="p-6 space-y-4">
            {/* The Hook */}
            {(section.data.hook || experience?.hook) && (
              <p className="text-lg italic text-muted-foreground">{section.data.hook || experience?.hook}</p>
            )}
            {/* The Experience */}
            <div>
              <h2 className="text-lg font-bold mb-2">The Experience</h2>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {section.data.content || experience?.experience_description || experience?.description || 'Add a description for your experience...'}
              </p>
            </div>
            {/* The Vibe */}
            {(section.data.vibe_tags || experience?.vibe_tags || []).length > 0 && (
              <div>
                <p className="text-sm font-semibold text-muted-foreground mb-2">The Vibe</p>
                <div className="flex flex-wrap gap-2">
                  {(section.data.vibe_tags || experience?.vibe_tags || []).map(tag => (
                    <span key={tag} className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">{tag}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      case 'includes':
        const items = section.data.items || [];
        return (
          <div className="p-6 bg-purple-50 dark:bg-purple-950">
            <h2 className="text-lg font-bold mb-3">{section.data.title || "What's Included"}</h2>
            <ul className="space-y-2">
              {items.filter(i => i).map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
              {items.length === 0 && (
                <li className="text-muted-foreground">Add included items...</li>
              )}
            </ul>
          </div>
        );
      
      case 'highlights':
        const highlights = section.data.highlights || [];
        return (
          <div className="p-6">
            <h2 className="text-lg font-bold mb-3">{section.data.title || 'Highlights'}</h2>
            <div className="grid grid-cols-2 gap-4">
              {highlights.map((h, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="font-medium">{h.title}</p>
                    <p className="text-sm text-muted-foreground">{h.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'venue':
        return (
          <div className="p-6">
            <h2 className="text-lg font-bold mb-3">Location</h2>
            <div className="flex items-start gap-2 mb-4">
              <MapPin className="w-5 h-5 text-purple-600 mt-1" />
              <div>
                <p className="font-medium">{experience?.venue_name || 'Venue Name'}</p>
                <p className="text-muted-foreground text-sm">Denver, CO</p>
              </div>
            </div>
            {section.data.show_map && (
              <div className="h-40 bg-gray-200 rounded-lg flex items-center justify-center">
                <span className="text-muted-foreground">Map Preview</span>
              </div>
            )}
          </div>
        );
      
      case 'validity':
        return (
          <div className="p-6 bg-yellow-50 dark:bg-yellow-950">
            <h2 className="text-lg font-bold mb-3">Validity & Terms</h2>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-yellow-600" />
                <span>Valid until: {experience?.valid_until ? format(new Date(experience.valid_until), 'MMMM d, yyyy') : 'No expiration'}</span>
              </div>
              {experience?.terms && (
                <p className="text-sm text-muted-foreground mt-2">{experience.terms}</p>
              )}
            </div>
          </div>
        );
      
      case 'howto':
        const steps = section.data.steps || [];
        return (
          <div className="p-6">
            <h2 className="text-lg font-bold mb-3">{section.data.title || 'How to Redeem'}</h2>
            <div className="space-y-3">
              {steps.map((step, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <span className="w-6 h-6 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                    {idx + 1}
                  </span>
                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'faq':
        const faqs = section.data.faqs || [];
        return (
          <div className="p-6">
            <h2 className="text-lg font-bold mb-3">{section.data.title || 'FAQ'}</h2>
            <div className="space-y-3">
              {faqs.filter(f => f.question).map((faq, idx) => (
                <div key={idx} className="border-b pb-3">
                  <p className="font-medium">{faq.question}</p>
                  <p className="text-muted-foreground text-sm mt-1">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        );
      
      case 'gallery':
        const images = section.data.images || [];
        return (
          <div className="p-6">
            <h2 className="text-lg font-bold mb-3">Gallery</h2>
            <div className="grid grid-cols-3 gap-2">
              {images.map((url, idx) => (
                <div key={idx} className="aspect-square rounded-lg overflow-hidden">
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div 
      className={cn(
        "bg-white dark:bg-gray-900 rounded-lg shadow-lg overflow-hidden transition-all",
        viewMode === 'mobile' ? 'w-[375px]' : 'w-full'
      )}
    >
      {sections
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <div key={section.id}>
            {getSectionContent(section)}
          </div>
        ))}
      
      {/* CTA Section */}
      <div className="p-6 bg-gray-50 dark:bg-gray-800 border-t">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-2xl font-bold">${experience?.price || '0'}</p>
            {experience?.gold_price && (
              <p className="text-sm text-yellow-600">
                <Star className="w-3 h-3 inline mr-1" />
                Gold: ${experience.gold_price}
              </p>
            )}
          </div>
          <Button className="bg-purple-600 hover:bg-purple-700">
            <Gift className="w-4 h-4 mr-2" />
            Buy Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Component
const ExperiencePageBuilder = () => {
  const navigate = useNavigate();
  const { experienceId } = useParams();
  const { isAuthenticated, isPartner, user, loading: authLoading } = useAuth();
  const [experience, setExperience] = useState(null);
  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [selectedSection, setSelectedSection] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [seoSettings, setSeoSettings] = useState({
    custom_url: '',
    meta_title: '',
    meta_description: '',
    og_image: ''
  });

  useEffect(() => {
    if (authLoading) return;
    
    // Allow both partners and admins
    if (!isAuthenticated || (!isPartner && user?.role !== 'admin')) {
      navigate('/auth');
      return;
    }
    
    fetchExperience();
  }, [isAuthenticated, isPartner, authLoading, experienceId, user]);

  const fetchExperience = async () => {
    try {
      const response = await api.get(`/vouchers/${experienceId}`);
      setExperience(response.data);
      
      // Load saved sections if available
      if (response.data.page_sections) {
        setSections(response.data.page_sections);
      } else {
        // Initialize with default sections using experience data
        setSections([
          { 
            id: 'hero-1', 
            type: 'hero', 
            order: 0,
            data: { 
              title: response.data.title, 
              image_url: response.data.images?.[0] || '', 
              overlay_opacity: 40 
            } 
          },
          { 
            id: 'about-1', 
            type: 'about', 
            order: 1,
            data: { 
              hook: response.data.hook || '',
              content: response.data.experience_description || response.data.description || '',
              vibe_tags: response.data.vibe_tags || []
            } 
          },
          { id: 'includes-1', type: 'includes', order: 2, data: { title: "What's Included", items: response.data.whats_included || [] } },
          { id: 'venue-1', type: 'venue', order: 3, data: { show_map: true } },
        ]);
      }
      
      // Load SEO settings
      setSeoSettings({
        custom_url: response.data.custom_url || '',
        meta_title: response.data.meta_title || response.data.title,
        meta_description: response.data.meta_description || response.data.description?.substring(0, 160),
        og_image: response.data.og_image || ''
      });
    } catch (error) {
      console.error('Failed to fetch experience:', error);
      toast.error('Failed to load experience');
      navigate('/partner/experiences');
    } finally {
      setLoading(false);
    }
  };

  const addSection = (template) => {
    const newSection = {
      id: `${template.id}-${Date.now()}`,
      type: template.id,
      order: sections.length,
      data: {}
    };
    setSections([...sections, newSection]);
    setSelectedSection(newSection.id);
  };

  const removeSection = (sectionId) => {
    setSections(sections.filter(s => s.id !== sectionId));
    if (selectedSection === sectionId) {
      setSelectedSection(null);
    }
  };

  const updateSectionData = (sectionId, data) => {
    setSections(sections.map(s => 
      s.id === sectionId ? { ...s, data } : s
    ));
  };

  const moveSection = (sectionId, direction) => {
    const index = sections.findIndex(s => s.id === sectionId);
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === sections.length - 1)
    ) return;

    const newSections = [...sections];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    
    newSections.forEach((s, i) => s.order = i);
    setSections(newSections);
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await api.put(`/vouchers/${experienceId}/page`, {
        page_sections: sections,
        custom_url: seoSettings.custom_url,
        meta_title: seoSettings.meta_title,
        meta_description: seoSettings.meta_description,
        og_image: seoSettings.og_image
      });
      // Also sync hook/vibe_tags/experience_description to the main voucher record
      const aboutSection = sections.find(s => s.type === 'about');
      if (aboutSection?.data) {
        const updates = {};
        if (aboutSection.data.hook) updates.hook = aboutSection.data.hook;
        if (aboutSection.data.content) updates.experience_description = aboutSection.data.content;
        if (aboutSection.data.vibe_tags) updates.vibe_tags = aboutSection.data.vibe_tags;
        if (Object.keys(updates).length > 0) {
          await api.put(`/vouchers/${experienceId}`, updates).catch(() => {});
        }
      }
      toast.success('Experience page saved!');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const getSectionEditor = (section) => {
    const props = {
      data: section.data,
      onChange: (data) => updateSectionData(section.id, data)
    };

    switch (section.type) {
      case 'hero': return <HeroEditor {...props} />;
      case 'about': return <AboutEditor {...props} />;
      case 'includes': return <IncludesEditor {...props} />;
      case 'highlights': return <HighlightsEditor {...props} />;
      case 'howto': return <HowToEditor {...props} />;
      case 'faq': return <FAQEditor {...props} />;
      case 'gallery': return <GalleryEditor {...props} />;
      case 'venue':
      case 'validity':
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p>This section automatically uses your experience data.</p>
            <p className="text-sm mt-2">Edit experience details to update this section.</p>
          </div>
        );
      default:
        return <p className="text-muted-foreground">No editor available for this section type.</p>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      {/* Header */}
      <header className="h-16 bg-white dark:bg-gray-900 border-b fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(`/partner/experiences/${experienceId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="h-8 w-px bg-gray-200 dark:bg-gray-700" />
          <h1 className="font-bold text-foreground">Experience Page Builder</h1>
        </div>
        
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
            <Button 
              variant={viewMode === 'desktop' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('desktop')}
            >
              <Monitor className="w-4 h-4" />
            </Button>
            <Button 
              variant={viewMode === 'mobile' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('mobile')}
            >
              <Smartphone className="w-4 h-4" />
            </Button>
          </div>

          {/* SEO Settings */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                SEO
              </Button>
            </SheetTrigger>
            <SheetContent>
              <SheetHeader>
                <SheetTitle>SEO Settings</SheetTitle>
              </SheetHeader>
              <div className="space-y-4 mt-6">
                <div>
                  <Label>Custom URL</Label>
                  <div className="flex items-center mt-1">
                    <span className="text-sm text-muted-foreground bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-l-md border border-r-0">
                      eventgoers.com/exp/
                    </span>
                    <Input 
                      className="rounded-l-none"
                      placeholder="your-experience-name"
                      value={seoSettings.custom_url}
                      onChange={(e) => setSeoSettings({ ...seoSettings, custom_url: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Meta Title</Label>
                  <Input 
                    value={seoSettings.meta_title}
                    onChange={(e) => setSeoSettings({ ...seoSettings, meta_title: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {seoSettings.meta_title?.length || 0}/60 characters
                  </p>
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <Textarea 
                    rows={3}
                    value={seoSettings.meta_description}
                    onChange={(e) => setSeoSettings({ ...seoSettings, meta_description: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {seoSettings.meta_description?.length || 0}/160 characters
                  </p>
                </div>
                <div>
                  <Label>Social Share Image</Label>
                  <Input 
                    placeholder="https://example.com/og-image.jpg"
                    value={seoSettings.og_image}
                    onChange={(e) => setSeoSettings({ ...seoSettings, og_image: e.target.value })}
                  />
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Button variant="outline" onClick={() => window.open(`/experiences/${experienceId}`, '_blank')}>
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </Button>
          
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={saveChanges}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="pt-16 flex">
        {/* Left Panel - Section List */}
        <aside className="w-72 bg-white dark:bg-gray-900 border-r fixed left-0 top-16 bottom-0 overflow-y-auto">
          <div className="p-4 border-b">
            <h2 className="font-bold text-foreground">Page Sections</h2>
            <p className="text-sm text-muted-foreground">Drag to reorder</p>
          </div>
          
          <div className="p-2 space-y-1">
            {sections
              .sort((a, b) => a.order - b.order)
              .map((section) => {
                const template = SECTION_TEMPLATES.find(t => t.id === section.type);
                const Icon = template?.icon || FileText;
                
                return (
                  <div 
                    key={section.id}
                    className={cn(
                      "p-3 rounded-lg cursor-pointer flex items-center gap-3 group transition-colors",
                      selectedSection === section.id 
                        ? "bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800" 
                        : "hover:bg-gray-50 dark:hover:bg-gray-800"
                    )}
                    onClick={() => setSelectedSection(section.id)}
                  >
                    <GripVertical className="w-4 h-4 text-gray-400 cursor-grab" />
                    <Icon className="w-4 h-4 text-gray-500" />
                    <span className="flex-1 text-sm font-medium">{template?.name || section.type}</span>
                    <div className="opacity-0 group-hover:opacity-100 flex gap-1">
                      <button onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'up'); }}>
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); moveSection(section.id, 'down'); }}>
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); removeSection(section.id); }}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Add Section */}
          <div className="p-4 border-t">
            <p className="text-sm font-medium mb-3">Add Section</p>
            <div className="grid grid-cols-2 gap-2">
              {SECTION_TEMPLATES.map((template) => {
                const Icon = template.icon;
                return (
                  <button
                    key={template.id}
                    onClick={() => addSection(template)}
                    className="p-3 border rounded-lg text-left hover:border-purple-500 hover:bg-purple-50 dark:hover:bg-purple-950 transition-colors"
                  >
                    <Icon className="w-4 h-4 text-purple-600 mb-1" />
                    <p className="text-xs font-medium">{template.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </aside>

        {/* Center - Preview */}
        <main className="flex-1 ml-72 mr-80 p-6 flex justify-center">
          <LivePreview 
            experience={experience} 
            sections={sections} 
            viewMode={viewMode}
          />
        </main>

        {/* Right Panel - Section Editor */}
        <aside className="w-80 bg-white dark:bg-gray-900 border-l fixed right-0 top-16 bottom-0 overflow-y-auto">
          {selectedSection ? (
            <>
              <div className="p-4 border-b">
                <h2 className="font-bold text-foreground">Edit Section</h2>
                <p className="text-sm text-muted-foreground">
                  {SECTION_TEMPLATES.find(t => t.id === sections.find(s => s.id === selectedSection)?.type)?.name}
                </p>
              </div>
              <div className="p-4">
                {getSectionEditor(sections.find(s => s.id === selectedSection))}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Select a section to edit</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default ExperiencePageBuilder;
